import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import {
  Typography,
  Button,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  Switch,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
  Radio,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { updateCityConnects } from "../../../store/actions/userActions";
import { updateSubCityConnects } from "../../../store/actions/subUserActions";

const SetupJaxDetails = ({
  connectToCity,
  setConnectToCity,
  selectedCity,
  county,
  selectedCityUser,
  setSelectedCityUser,
  addNewUser,
  setAddNewUser,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [warningText, setWarningText] = useState(
    "Your credentials are securely encrypted and remain inaccessible to anyone. We prioritize your privacy and use top-tier data security measures to protect your information."
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleSelectUser = (row) => {
    if (selectedCityUser === row.id) {
      setSelectedCityUser("");
    } else {
      setSelectedCityUser(row.id);
    }
  };

  const handleRemoveUser = async (row) => {
    setWarningText("");
    try {
      setCityLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/remove-city-credential`,
        {
          currentSession: currentSession,
          county: county,
          city: selectedCity,
          id: row,
        },
        { withCredentials: true }
      );

      if (resp?.data?.success) {
        // await dispatch(updatePermitPackage(resp?.data?.data));
        if (currentSession === "userSession") {
          await dispatch(updateCityConnects(resp?.data?.data));
        } else {
          await dispatch(updateSubCityConnects(resp?.data?.data));
        }
        setSelectedCityUser("");
        setUsername("");
        setPassword("");
        setAgreeToTerms(false);
        setUsernameError(false);
        setPasswordError(false);
        setIsError(false);
        setWarningText(
          "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information."
        );
      }
      setAddNewUser(false);
      setCityLoading(false);
    } catch (e) {
      setWarningText(
        "Login failed. Please check your credentials and try again."
      );
      setIsError(true);
      setUsernameError(true);
      setPasswordError(true);
      setCityLoading(false);
    }
  };

  const handleAcceptCityScrape = async () => {
    setWarningText("");
    try {
      setCityLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/save-city_credentials`,
        {
          currentSession: currentSession,
          username: username,
          password: password,
          county: county,
          city: selectedCity,
        },
        { withCredentials: true }
      );

      if (resp?.data?.success) {
        // await dispatch(updatePermitPackage(resp?.data?.data));
        if (currentSession === "userSession") {
          await dispatch(updateCityConnects(resp?.data?.data));
        } else {
          await dispatch(updateSubCityConnects(resp?.data?.data));
        }
        setUsername("");
        setPassword("");
        setAgreeToTerms(false);
        setUsernameError(false);
        setPasswordError(false);
        setIsError(false);
        setWarningText(
          "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information."
        );
      }
      setAddNewUser(false);
      setCityLoading(false);
    } catch (e) {
      setWarningText(
        "Login failed. Please check your credentials and try again."
      );
      setIsError(true);
      setUsernameError(true);
      setPasswordError(true);
      setCityLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          height: "calc(100vh - 200px)",
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h5"
          mt={2}
          align="center"
          color="primary"
          gutterBottom
        >
          Automate Your Permit Process with the City of Jacksonville
        </Typography>

        <Typography
          variant="body2"
          fontWeight="bold"
          align="center"
          px={2}
          mb={2}
        >
          To save you time and reduce errors, Permit Rockstar offers full
          automation with the City of Jacksonville’s permit portal. If you
          enable this option, we’ll generate the permit number for you, pre-fill
          city forms using your selected data, and upload all required
          documents. This means you only need to review and submit. It’s fast,
          reliable, and completely secure.
        </Typography>

        <Box display="flex" justifyContent="center">
          <FormControlLabel
            control={
              <Switch
                checked={connectToCity}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setUsername("");
                    setPassword("");
                    setAgreeToTerms(false);
                    setUsernameError(false);
                    setPasswordError(false);
                    setIsError(false);
                    setWarningText(
                      "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information."
                    );
                    setAddNewUser(false);
                    setSelectedCityUser("");
                  }
                  setConnectToCity(e.target.checked);
                }}
              />
            }
            label="Enable Automation for This Permit Package"
          />
        </Box>

        {/* Conditionally render based on the automation switch */}
        {connectToCity ? (
          // If user already gave consent before
          (currentUser?.permit_consent?.[county.toLowerCase()]?.[
            selectedCity.toLowerCase()
          ]?.consent === true ? (<Box sx={{ mt: 4 }}>
            {/* You can render a mini table or a summary of saved city creds */}
            <Typography variant="h6" align="center" color="success.main">
              Your login credentials are already saved for this city.
            </Typography>
            <Typography
              align="center"
              fontWeight="bold"
              variant="body2"
              mt={0}
              mb={3}
            >
              If you’d like to use a different account, you can remove it
              below and enter new login details.
            </Typography>
            {/* Replace with actual component later */}
            <>[City Credentials Table / Remove Button / Replace Form]</>
          </Box>) : (<Box mt={3}>
            {/* Login form when no consent yet */}
            <Typography variant="h6" align="center" color="primary">
              Enter Your City Website Login
            </Typography>
            <Typography align="center" variant="body2" mt={1} mb={2}>
              We’ll use these credentials to log into the Jacksonville permit
              portal on your behalf and pre-fill everything for you.
            </Typography>
            <Button
              color={addNewUser ? "error" : "primary"}
              variant="text"
              sx={{ textDecoration: "underline" }}
              onClick={() => {
                setUsername("");
                setPassword("");
                setAgreeToTerms(false);
                setUsernameError(false);
                setPasswordError(false);
                setIsError(false);
                setWarningText(
                  "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information."
                );
                setSelectedCityUser("");

                setAddNewUser(!addNewUser);
              }}
            >
              {!addNewUser ? "Add New User" : "Cancel"}
            </Button>
            {!addNewUser ? (
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>City Portal Role</TableCell>
                      <TableCell>Added At</TableCell>
                      <TableCell align="right">Select</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentUser?.city_connects?.[county.toLowerCase()]?.[
                      selectedCity.toLowerCase()
                    ]
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row) => (
                        <TableRow key={row.id}>
                          <TableCell sx={{ pl: 0, py: 0.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {selectedCityUser !== row?.id ? (
                                <IconButton
                                  disabled={selectedCityUser !== row?.id}
                                  color="error"
                                  onClick={() => handleRemoveUser(row.id)} // replace with your handler
                                  aria-label="delete"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              ) : (
                                <Tooltip title="Remove User">
                                  <IconButton
                                    disabled={selectedCityUser !== row?.id}
                                    color="error"
                                    onClick={() => handleRemoveUser(row.id)} // replace with your handler
                                    aria-label="delete"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Typography variant="body2">
                                {row.user_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {row?.role
                              ?.replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </TableCell>
                          <TableCell sx={{ pl: 0, py: 0.5 }}>
                            {DateTime.fromISO(row.date, { zone: "utc" })
                              .setZone("America/New_York")
                              .toFormat("MM/dd/yyyy")}
                          </TableCell>
                          <TableCell sx={{ pl: 0, py: 0.5 }} align="right">
                            {/* <Button
                              size="small"
                              variant={
                                selectedCityUser === row?.id
                                  ? "contained"
                                  : "outlined"
                              }
                              color="primary"
                              onClick={() => handleSelectUser(row)}
                              sx={{ mr: 1, minWidth: "7rem" }}
                            >
                              {selectedCityUser === row?.id
                                ? "Selected"
                                : "Select"}
                            </Button> */}

                            <Radio
                              checked={selectedCityUser === row.id}
                              onChange={() => handleSelectUser(row)}
                              value={row.id}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={
                    currentUser?.city_connects?.[county.toLowerCase()]?.[
                      selectedCity.toLowerCase()
                    ].length || 0
                  }
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableContainer>
            ) : (
              <>
                <Box component="form" sx={{ mt: 3, px: 0.5 }}>
                  <TextField
                    error={usernameError}
                    helperText={
                      usernameError ? "Please enter a valid username" : ""
                    }
                    disabled={cityLoading}
                    label="City Website Username"
                    variant="filled"
                    fullWidth
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (usernameError) setUsernameError(false);
                      if (passwordError) setPasswordError(false);
                      if (
                        warningText !== "" &&
                        warningText ===
                          "Login failed. Please check your credentials and try again."
                      )
                        setWarningText("");
                      if (isError) setIsError(false);
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    error={passwordError}
                    helperText={
                      passwordError ? "Please enter a valid password" : ""
                    }
                    disabled={cityLoading}
                    label="City Website Password"
                    type="password"
                    variant="filled"
                    fullWidth
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (passwordError) setPasswordError(false);
                      if (usernameError) setUsernameError(false);
                      if (
                        warningText !== "" &&
                        warningText ===
                          "Login failed. Please check your credentials and try again."
                      )
                        setWarningText("");
                      if (isError) setIsError(false);
                    }}
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        name="agreeToTerms"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{" "}
                        <Link
                          href="https://permitrockstar.com//terms-and-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms and Conditions
                        </Link>
                      </Typography>
                    }
                  />

                  <Typography
                    align="center"
                    color="error"
                    variant="body2"
                    sx={{ mt: 2, fontWeight: "bold" }}
                  >
                    {warningText}
                  </Typography>
                </Box>

                <Box mt={3} sx={{ display: "flex", flexDirection: "column" }}>
                  <LoadingButton
                    disabled={
                      !username || !password || !agreeToTerms || isError
                    }
                    loading={cityLoading}
                    color="success"
                    onClick={handleAcceptCityScrape}
                    variant="contained"
                    fullWidth
                  >
                    Provide Consent
                  </LoadingButton>
                </Box>
              </>
            )}
          </Box>))
        ) : (
          <Typography align="center" color="text.secondary" mt={3}>
            You chose to skip automation for this permit package. You can
            proceed manually.
          </Typography>
        )}
      </Box>
    </>
  );
};

export default SetupJaxDetails;
